import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AuthContext from './AuthContext';
import { localDateString } from '../utils/date';

const NotificationContext = createContext();
export default NotificationContext;

export const NotificationProvider = ({ children }) => {
    const { authTokens, backendUrl, userRole } = useContext(AuthContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [toast, setToast] = useState(null);
    const toastQueue = useRef([]);
    const prevDriverStatuses = useRef({});
    const prevDriverDelays = useRef({});

    const showToast = useCallback((message, severity = 'info', link = null) => {
        setToast(prev => {
            if (prev) {
                toastQueue.current.push({ message, severity, link });
                return prev;
            }
            return { message, severity, link };
        });
    }, []);

    const handleClose = useCallback(() => {
        const next = toastQueue.current.shift();
        setToast(next || null);
    }, []);

    // Poll active-drivers for admin role — detect route status changes
    const fetchAndDetect = useCallback(async () => {
        if (!authTokens?.access) return;
        try {
            const res = await fetch(backendUrl + 'api/active-drivers/', {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + authTokens.access,
                },
            });
            if (!res.ok) return;
            const data = await res.json();
            const prev = prevDriverStatuses.current;
            const prevDelays = prevDriverDelays.current;
            for (const driver of data) {
                const driverName = `${driver.firstname} ${driver.lastname}`;
                const routeName = driver.route_name || '';
                const prevStatus = prev[driver.profile_id];
                const newStatus = driver.route_status;
                if (prevStatus === undefined) {
                    if (newStatus === 'active') {
                        // showToast(t('map.route_started', { driver: driverName, route: routeName }), 'info');
                    }
                } else if (prevStatus !== newStatus) {
                    if (newStatus === 'active') {
                        // showToast(t('map.route_started', { driver: driverName, route: routeName }), 'info');
                    } else if (newStatus === 'stopped') {
                        // showToast(t('map.route_stopped', { driver: driverName, route: routeName }), 'warning');
                    } else if (newStatus === 'finished') {
                        // showToast(t('map.route_finished', { driver: driverName, route: routeName }), 'success');
                    }
                }
                prev[driver.profile_id] = newStatus;

                // Detect delay/early changes
                const newDelay = driver.delay_minutes ?? null;
                const oldDelay = prevDelays[driver.profile_id] ?? null;
                if (oldDelay !== newDelay) {
                    if (newDelay > 0) {
                        // showToast(t('map.delay_set', { driver: driverName, minutes: newDelay }), 'warning');
                    } else if (newDelay < 0) {
                        // showToast(t('map.early_set', { driver: driverName, minutes: Math.abs(newDelay) }), 'success');
                    }
                }
                prevDelays[driver.profile_id] = newDelay;
            }
            const activeIds = new Set(data.map(d => d.profile_id));
            for (const id of Object.keys(prev)) {
                if (!activeIds.has(id)) delete prev[id];
            }
            for (const id of Object.keys(prevDelays)) {
                if (!activeIds.has(id)) delete prevDelays[id];
            }
        } catch (_) {}
    }, [backendUrl, authTokens, t, showToast]);

    const POLL_INTERVAL = parseInt(process.env.REACT_APP_NOTIFICATION_POLL_INTERVAL_MS) || 15000;

    useEffect(() => {
        if (userRole !== 0) return;
        fetchAndDetect();
        const interval = setInterval(fetchAndDetect, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchAndDetect, userRole, POLL_INTERVAL]);

    // Birthday notification — shown once per day for admin role
    useEffect(() => {
        if (userRole !== 0 || !authTokens?.access) return;
        const today = localDateString();
        if (localStorage.getItem('birthday_notif_date') === today) return;
        fetch(backendUrl + 'api/reports/birthdays/', {
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authTokens.access },
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (!data || data.count === 0) return;
                localStorage.setItem('birthday_notif_date', today);
                const msg = data.count === 1
                    ? t('reports.birthday_notification', { count: data.count })
                    : t('reports.birthday_notification_plural', { count: data.count });
                showToast(msg, 'info', '/reports?tab=3');
            })
            .catch(() => {});
    }, [userRole, authTokens, backendUrl, t, showToast]);

    // Poll pending toasts sent by admin — for all authenticated users
    const fetchPendingToasts = useCallback(async () => {
        if (!authTokens?.access) return;
        try {
            const res = await fetch(backendUrl + 'api/toast/pending/', {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + authTokens.access,
                },
            });
            if (!res.ok) return;
            const data = await res.json();
            for (const item of data) {
                showToast(item.message, item.severity);
            }
        } catch (_) {}
    }, [backendUrl, authTokens, showToast]);

    useEffect(() => {
        if (!authTokens?.access) return;
        fetchPendingToasts();
        const interval = setInterval(fetchPendingToasts, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchPendingToasts, authTokens, POLL_INTERVAL]);

    return (
        <NotificationContext.Provider value={{ showToast }}>
            {children}
            <Snackbar
                open={!!toast}
                autoHideDuration={20000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                {toast ? (
                    <Alert
                        onClose={handleClose}
                        severity={toast.severity}
                        variant="filled"
                        onClick={toast.link ? () => { navigate(toast.link); handleClose(); } : undefined}
                        sx={{
                            fontFamily: '"Outfit", sans-serif',
                            fontWeight: 600,
                            cursor: toast.link ? 'pointer' : 'default',
                        }}
                    >
                        {toast.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </NotificationContext.Provider>
    );
};
